import { getCycles } from "@/services/schoolService";
import { useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import StatCard from "@/components/base/StatCard";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { computeTotals, deleteTransaction, getCategories, getTransactions } from "@/services/financeService";
import { useAuth } from "@/contexts/AuthContext";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import PeriodFilter from "@/components/feature/PeriodFilter";
import { ALL_PERIOD, inPeriod, periodLabel, type Period } from "@/utils/period";
import { formatMoney, formatDate } from "@/utils/format";
import type { Transaction } from "@/types";
import TransactionModal from "../finance/components/TransactionModal";
import CategoryManager from "../finance/components/CategoryManager";
import { cycleIdOf } from "@/utils/cycleScope";

async function load() {
  const [transactions, categories, cycles] = await Promise.all([getTransactions(), getCategories(), getCycles()]);
  return { transactions, categories, cycles };
}

export default function RevenuePage() {
  const { notify } = useToast();
  const { allowedCycleIds, can } = useAuth();
  const { data, loading, reload } = useAsync(load, []);
  const [cycleFilter, setCycleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState<Period>(ALL_PERIOD);
  const pager = usePager();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);

  if (loading || !data) return <LoadingState label="Chargement des recettes..." />;
  const { transactions, categories, cycles } = data;
  const primaireId = cycleIdOf(cycles, "PRIMAIRE");

  const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
  const scopedTransactions = transactions.filter((t) => allowedCycleIds.includes(t.cycleId));

  const catName = (id: string) => categories.find((c) => c.id === id)?.name ?? "—";
  const catIcon = (id: string) => categories.find((c) => c.id === id)?.icon ?? "ri-price-tag-3-line";
  const cycleLabel = (id: string) => cycles.find((c) => c.id === id)?.label ?? "—";

  const revenues = scopedTransactions.filter((t) => t.type === "recette" && inPeriod(t.date, period));
  const totals = computeTotals(revenues);
  const filtered = revenues
    .filter((t) => cycleFilter === "all" || t.cycleId === cycleFilter)
    .filter((t) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return t.description.toLowerCase().includes(q) || catName(t.categoryId).toLowerCase().includes(q);
    })
    .sort((a, b) => b.date.localeCompare(a.date));
  const page = pager.slice(filtered, `${cycleFilter}|${search}|${JSON.stringify(period)}`);

  const handleDelete = async (t: Transaction) => {
    await deleteTransaction(t.id);
    await reload();
    notify("Recette supprimée.", "info");
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Recettes"
        subtitle="Catégories : Scolarité, Inscription, Cantine, Dons et autres — séparées par cycle"
        icon="ri-arrow-up-circle-line"
        actions={
          can("revenues:create") ? (
            <Button
              icon="ri-add-line"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              Enregistrer une recette
            </Button>
          ) : undefined
        }
      />

      <CycleScopeBanner className="mb-5" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Total des recettes" value={formatMoney(totals.recettes)} icon="ri-arrow-up-circle-line" tone="primary" hint={periodLabel(period)} />
        <StatCard label="Nombre d'encaissements" value={String(revenues.length)} icon="ri-file-list-3-line" tone="secondary" />
        <StatCard label="Catégories de recettes" value={String(categories.filter((c) => c.type === "recette").length)} icon="ri-price-tag-3-line" tone="accent" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" padded={false}>
          <div className="flex flex-col gap-3 border-b border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-sm font-semibold font-heading text-foreground-950">
              Encaissements ({filtered.length})
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-36 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <PeriodFilter value={period} onChange={setPeriod} />
              <select
                value={cycleFilter}
                onChange={(e) => setCycleFilter(e.target.value)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm cursor-pointer focus:outline-none"
              >
                <option value="all">Tous les cycles</option>
                {visibleCycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Catégorie</th>
                  <th className="px-4 py-3 font-medium">Cycle</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium text-right">Montant</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((t) => (
                  <tr key={t.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                    <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(t.date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                          <i className={`${catIcon(t.categoryId)} text-sm`}></i>
                        </span>
                        <span className="text-foreground-800">{catName(t.categoryId)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={t.cycleId === primaireId ? "warning" : "secondary"}>{cycleLabel(t.cycleId)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-600 max-w-[220px] truncate">{t.description}</td>
                    <td className="px-4 py-3 text-right font-medium text-primary-700 whitespace-nowrap">{formatMoney(t.amount)}</td>
                    <td className="px-4 py-3">
                      {t.source ? (
                        <span className="flex justify-end text-xs text-foreground-400" title="Opération automatique : annulez le reçu ou la paie pour la retirer">
                          <i className="ri-lock-line mr-1"></i>{t.source === "receipt" ? "Reçu" : "Paie"}
                        </span>
                      ) : (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditing(t);
                            setModalOpen(true);
                          }}
                          className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                          aria-label="Modifier"
                          style={can("revenues:update") ? undefined : { display: "none" }}
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(t)}
                          className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                          aria-label="Supprimer"
                          style={can("revenues:delete") ? undefined : { display: "none" }}
                        >
                          <i className="ri-delete-bin-6-line"></i>
                        </button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-foreground-400">
                      Aucune recette pour ces filtres ou cette période.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination pager={page} className="border-t border-background-200" />
        </Card>

        <CategoryManager type="recette" categories={categories} cycles={visibleCycles} onChanged={reload} />
      </div>

      <TransactionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={reload}
        type="recette"
        categories={categories}
        cycles={visibleCycles}
        editing={editing}
      />
    </div>
  );
}